import axios from 'axios';
import api from './api';
import { BuylistItem, BuylistOrder, BuylistFilters, PriceFilter, PriceRangeResponse, PagedResponse } from '../types';
import {
  isMockMode,
  mockFetchBuylistItems,
  mockFetchFeaturedCards,
  mockFetchPriceRange,
  mockFetchExpansionNames,
  mockSearchBuylistItems,
  mockGetBuylistAutocomplete,
  mockFetchBuylistItemById,
} from './mockData';

const API_URL = (window as any).__ENV__?.VITE_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:8080';

export const getBuylistImageUrl = (cardBlueprintId: number, imageUrl?: string): string => {
  if (imageUrl) return imageUrl;
  return `${API_URL}/image-proxy/${cardBlueprintId}`;
};

/**
 * Build query params object from filter state, only including active filters.
 */
const buildFilterParams = (
  filters?: BuylistFilters,
  priceFilter?: PriceFilter,
  expansionName?: string
): Record<string, boolean | number | string> => {
  const params: Record<string, boolean | number | string> = {};
  if (filters) {
    if (filters.reservedList) params.reservedList = true;
    if (filters.gameChanger) params.gameChanger = true;
    if (filters.topCommander) params.topCommander = true;
    if (filters.topEdhCard) params.topEdhCard = true;
    if (filters.premodernStaple) params.premodernStaple = true;
    if (filters.modernStaple) params.modernStaple = true;
  }
  if (expansionName) params.expansionName = expansionName;
  if (priceFilter?.minPrice !== undefined) params.minPrice = priceFilter.minPrice;
  if (priceFilter?.maxPrice !== undefined) params.maxPrice = priceFilter.maxPrice;
  return params;
};

// Fetch distinct expansion names available on the buylist
export const fetchExpansionNames = async (): Promise<string[]> => {
  if (isMockMode()) return mockFetchExpansionNames();
  try {
    const response = await axios.get(`${API_URL}/buylist/expansions`);
    return response.data;
  } catch (error) {
    console.error('Error fetching expansion names:', error);
    return [];
  }
};

// Fetch available price range bounds
export const fetchPriceRange = async (): Promise<PriceRangeResponse> => {
  if (isMockMode()) return mockFetchPriceRange();
  try {
    const response = await axios.get(`${API_URL}/buylist/price-range`);
    return response.data;
  } catch (error) {
    console.error('Error fetching buylist price range:', error);
    throw error;
  }
};

// Fetch paginated buylist items
export const fetchBuylistItems = async (
  page = 0,
  size = 20,
  sortBy = 'buyMultiplier',
  sortDirection = 'desc',
  filters?: BuylistFilters,
  priceFilter?: PriceFilter,
  expansionName?: string
): Promise<PagedResponse<BuylistItem>> => {
  if (isMockMode()) return mockFetchBuylistItems(page, size, sortBy, sortDirection, filters, priceFilter, expansionName);
  try {
    const response = await axios.get(`${API_URL}/buylist/items`, {
      params: { page, size, sortBy, sortDirection, ...buildFilterParams(filters, priceFilter, expansionName) }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching buylist items:', error);
    throw error;
  }
};

// Fetch buylist item by ID
export const fetchBuylistItemById = async (id: number): Promise<BuylistItem> => {
  if (isMockMode()) return mockFetchBuylistItemById(id);
  try {
    const response = await axios.get(`${API_URL}/buylist/items/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching buylist item with ID ${id}:`, error);
    throw error;
  }
};

// Submit a buylist order (authenticated — the shared api instance attaches the Shopify token)
export const submitBuylistOrder = async (order: BuylistOrder): Promise<any> => {
  try {
    const response = await api.post('/buylist/order', order);
    return response.data;
  } catch (error) {
    console.error('Error submitting buylist order:', error);
    throw error;
  }
};

// Submit a guest buylist order (no authentication)
export const submitGuestBuylistOrder = async (order: BuylistOrder): Promise<any> => {
  try {
    const response = await axios.post(`${API_URL}/buylist/order/gjest`, order);
    return response.data;
  } catch (error) {
    console.error('Error submitting guest buylist order:', error);
    throw error;
  }
};

// Search buylist items - returns full BuylistItem objects
export const searchBuylistItems = async (
  query: string,
  filters?: BuylistFilters,
  priceFilter?: PriceFilter,
  expansionName?: string
): Promise<BuylistItem[]> => {
  if (isMockMode()) return mockSearchBuylistItems(query, filters, priceFilter, expansionName);
  try {
    const response = await axios.get(`${API_URL}/buylist/search`, {
      params: { name: query, ...buildFilterParams(filters, priceFilter, expansionName) }
    });
    return response.data;
  } catch (error) {
    console.error('Error searching buylist items:', error);
    throw error;
  }
};

// Get autocomplete suggestions for buylist search
// Upload CSV file for buylist matching
export const uploadCsvForMatching = async (
  file: File,
  kilde: 'manabox' | 'deckbox'
): Promise<BuylistItem[]> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('kilde', kilde);

    const response = await axios.post(`${API_URL}/buylist/csv-match`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading CSV for matching:', error);
    throw error;
  }
};

// Match a Moxfield-format deck list (paste from "Copy for Moxfield") against the buylist
export const matchMoxfieldTekst = async (tekst: string): Promise<BuylistItem[]> => {
  try {
    const formData = new FormData();
    formData.append('tekst', tekst);
    const response = await axios.post(`${API_URL}/buylist/moxfield-match`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    console.error('Error matching Moxfield deck list:', error);
    throw error;
  }
};

// Fetch top N featured buylist cards for a given category, sorted by highest buyPrice
export type FeaturedCategory = 'topCommander' | 'topEdhCard' | 'premodernStaple' | 'modernStaple';

export const fetchFeaturedCards = async (
  category: FeaturedCategory,
  count = 5
): Promise<BuylistItem[]> => {
  if (isMockMode()) return mockFetchFeaturedCards(category, count);
  const filters: BuylistFilters = {
    reservedList: false,
    gameChanger: false,
    topCommander: category === 'topCommander',
    topEdhCard: category === 'topEdhCard',
    premodernStaple: category === 'premodernStaple',
    modernStaple: category === 'modernStaple',
  };
  const response = await fetchBuylistItems(0, count, 'buyPrice', 'desc', filters);
  return response.content;
};

// Hent topp-rangerte kort for et format, sortert etter rangering
export const fetchFeaturedByFormat = async (
  format: 'premodern' | 'modern' | 'commander' | 'edhcard',
  limit = 5
): Promise<BuylistItem[]> => {
  if (isMockMode()) {
    const formatToCategoryMap: Record<string, string> = {
      commander: 'topCommander',
      edhcard: 'topEdhCard',
      premodern: 'premodernStaple',
      modern: 'modernStaple',
    };
    return mockFetchFeaturedCards(formatToCategoryMap[format] || format, limit);
  }
  try {
    const response = await axios.get(`${API_URL}/buylist/featured/${format}`, {
      params: { limit },
    });
    return response.data;
  } catch (error) {
    console.error(`Feil ved henting av featured ${format}-kort:`, error);
    return [];
  }
};

export const getBuylistAutocomplete = async (query: string): Promise<string[]> => {
  if (isMockMode()) return mockGetBuylistAutocomplete(query);
  try {
    if (!query.trim()) return [];
    
    // Make the API request
    const response = await axios.get(
      `${API_URL}/buylist/autocomplete/search?name=${encodeURIComponent(query)}`
    );
    
    console.log('Raw response data:', response.data);
    
    // Process the response based on its structure
    let results: string[] = [];
    
    if (Array.isArray(response.data)) {
      // If response.data is already an array
      if (response.data.length > 0) {
        if (typeof response.data[0] === 'string') {
          // If it's an array of strings
          results = response.data;
        } else if (typeof response.data[0] === 'object' && response.data[0] !== null) {
          // If it's an array of objects, try to extract blueprintName
          results = response.data
            .filter(item => item && item.blueprintName)
            .map(item => item.blueprintName);
        }
      }
    } else if (response.data && typeof response.data === 'object') {
      // If it's an object, try to find an array inside it
      Object.keys(response.data).forEach(key => {
        const value = response.data[key];
        if (Array.isArray(value) && value.length > 0) {
          if (typeof value[0] === 'string') {
            results = value;
          }
        }
      });
    }
    
    console.log('Processed results:', results);
    return results.slice(0, 10); // Limit to top 10
  } catch (error) {
    console.error('Error fetching autocomplete suggestions:', error);
    return [];
  }
};
